import {
  Box,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  ListItem,
  Text,
  Textarea,
  UnorderedList,
  VStack
} from '@chakra-ui/react'
import { FormErrorDisplay } from '@components'
import {
  ArrowForwardIcon,
  CalendarIcon,
  EditPencil,
  MapMarkerIcon,
  RecurringIcon,
  VideoCamera
} from '@components/icons'
import { Field, type FieldProps, type FormikProps } from 'formik'
import moment from 'moment'
import React, { useState } from 'react'
import { useCalendarEvent } from '../../context/calendar-event.context'
import { EVENT_SETTING_TELEHEALTH } from '../../models'
import { BPDatePicker } from '../date-picker'
import { BPSelect } from '../select'
import { BPTimePicker } from '../time-picker'
import type { AddEditEventFormValues } from './add-edit-event-modal'

interface OtherEventFormProps {
  formikProps: FormikProps<AddEditEventFormValues>
  settingOptions: { value: string; label: string }[]
  recurringOptions: { value: string; label: string }[]
}

export const OtherEventForm: React.FC<OtherEventFormProps> = ({
  formikProps,
  settingOptions,
  recurringOptions
}) => {
  const [isAllDayEvent, setIsAllDayEvent] = useState(false)
  const { currentEvent } = useCalendarEvent()

  if (!currentEvent) {
    return null
  }

  const { errors, setFieldValue, touched } = formikProps

  return (
    <Box py="16px">
      <Field name="eventType">
        {({ field }: FieldProps) => <Input type="hidden" {...field} />}
      </Field>
      <Field name="patientId">
        {({ field }: FieldProps) => <Input type="hidden" {...field} />}
      </Field>
      <Field name="subject">
        {({ field }: FieldProps) => (
          <InputGroup>
            <InputLeftElement>
              <EditPencil name="search" />
            </InputLeftElement>
            <Input
              type="text"
              {...field}
              border="none"
              borderBottom="1px solid rgba(55, 60, 65, 0.14)"
              _focus={{
                outline: 'none',
                borderBottom: '1px solid var(--chakra-colors-primary)'
              }}
              fontSize="24px"
            />
          </InputGroup>
        )}
      </Field>
      <Flex pt="16px" gap="12px" direction="column">
        <Box>
          <Text fontSize="12px" fontWeight="bold">
            Edit event details
          </Text>
        </Box>
        <VStack gap="4px" pb="12px">
          <HStack gap="8px" width="100%">
            <Field name="eventDate" type="date">
              {({ field }: FieldProps) => (
                <>
                  <Box padding="4px">
                    <CalendarIcon width="24px" height="24px" />
                  </Box>
                  <FormControl
                    isInvalid={!!errors.eventDate && !!touched.eventDate}
                    margin="0px"
                  >
                    <BPDatePicker
                      selected={field.value}
                      onChange={date => {
                        setFieldValue(field.name, date)
                      }}
                      errors={errors.eventDate}
                    />
                  </FormControl>
                </>
              )}
            </Field>
            {isAllDayEvent ? (
              <>
                <Field name="eventStartTime" type="time">
                  {({ field }: FieldProps) => (
                    <Input type="hidden" {...field} />
                  )}
                </Field>
                <Field name="eventEndTime" type="time">
                  {({ field }: FieldProps) => (
                    <Input type="hidden" {...field} />
                  )}
                </Field>
              </>
            ) : (
              <HStack>
                <Field name="eventStartTime" type="time">
                  {({ field }: FieldProps) => (
                    <FormControl
                      isInvalid={
                        !!errors.eventStartTime && !!touched.eventStartTime
                      }
                    >
                      <BPTimePicker
                        {...field}
                        onChange={(value: string) => {
                          setFieldValue(field.name, value)
                        }}
                        errors={errors.eventStartTime}
                      />
                    </FormControl>
                  )}
                </Field>
                <Box padding="4px">
                  <ArrowForwardIcon />
                </Box>
                <Field name="eventEndTime" type="time">
                  {({ field }: FieldProps) => (
                    <FormControl
                      isInvalid={
                        !!errors.eventEndTime && !!touched.eventEndTime
                      }
                    >
                      <BPTimePicker
                        {...field}
                        onChange={(value: string) => {
                          setFieldValue(field.name, value)
                        }}
                        errors={errors.eventEndTime}
                      />
                    </FormControl>
                  )}
                </Field>
              </HStack>
            )}
          </HStack>
          <HStack gap="8px" alignItems="flex-start" width="100%">
            <Field name="setting">
              {({ field }: FieldProps) => (
                <>
                  <Box padding="12px 4px">
                    {field.value === 'in-person' && (
                      <MapMarkerIcon
                        width="24px"
                        height="24px"
                        fill="#A1A1A1"
                      />
                    )}
                    {field.value === EVENT_SETTING_TELEHEALTH && (
                      <VideoCamera width="24px" fill="#A1A1A1" />
                    )}
                  </Box>
                  <FormControl
                    isInvalid={!!errors.setting && !!touched.setting}
                  >
                    <BPSelect
                      selectedValue={field.value}
                      onChange={(value: any) =>
                        setFieldValue(field.name, value)
                      }
                      options={settingOptions}
                      errors={errors.setting}
                    />
                  </FormControl>
                </>
              )}
            </Field>
          </HStack>
          <HStack gap="8px" alignItems="flex-start" width="100%">
            <Field name="recurring">
              {({ field }: FieldProps) => (
                <>
                  <Box padding="12px 4px">
                    <RecurringIcon
                      width="24px"
                      height="24px"
                      fill={
                        field.value === 'no-repeat'
                          ? '#A1A1A1'
                          : 'var(--chakra-colors-primary)'
                      }
                    />
                  </Box>
                  <FormControl
                    isInvalid={!!errors.recurring && !!touched.recurring}
                  >
                    <BPSelect
                      selectedValue={field.value}
                      onChange={(value: string) =>
                        setFieldValue(field.name, value)
                      }
                      options={recurringOptions}
                      errors={errors.recurring}
                    />
                  </FormControl>
                </>
              )}
            </Field>
            {formikProps.values.recurring !== 'no-repeat' && (
              <Field name="occurrenceCount">
                {({ field }: FieldProps) => (
                  <FormControl
                    isInvalid={
                      !!formikProps.errors.occurrenceCount &&
                      !!formikProps.touched.occurrenceCount
                    }
                    flex="1"
                  >
                    <Input
                      {...field}
                      type="number"
                      min="1"
                      placeholder="Count"
                      size="sm"
                    />
                  </FormControl>
                )}
              </Field>
            )}
          </HStack>
          <Box width="100%">
            <FormLabel display="flex" alignItems="center" gap="8px">
              <Checkbox
                ml="8px"
                mr="16px"
                onChange={e => {
                  if (e.target.checked) {
                    setFieldValue('eventStartTime', '12:00 am')
                    setFieldValue('eventEndTime', '11:59 pm')
                  } else {
                    setFieldValue(
                      'eventStartTime',
                      moment(currentEvent.start).format('hh:mm a')
                    )
                    setFieldValue(
                      'eventEndTime',
                      moment(currentEvent.end).format('hh:mm a')
                    )
                  }
                  setIsAllDayEvent(e.target.checked)
                }}
              />
              <Text>All day event</Text>
            </FormLabel>
          </Box>
        </VStack>
        <Box>
          <Text fontSize="12px">
            <b>Description</b> (optional)
          </Text>
        </Box>
        <Field name="description" type="text">
          {({ field }: FieldProps) => (
            <FormControl
              isInvalid={!!errors.description && !!touched.description}
            >
              <Textarea
                {...field}
                placeholder="A brief summary of the event"
                borderColor="pale_gray"
              />
            </FormControl>
          )}
        </Field>
      </Flex>
      <FormErrorDisplay errors={errors} />
    </Box>
  )
}
